import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Power, PowerOff, Unlink } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  UserAvatar,
  cn,
} from "@nexus/ui";
import {
  AdminFilterBar,
  AdminSearchInput,
} from "@/features/admin/components/layout";
import { sortTableRows, toggleUsageSort } from "@/features/admin/lib/table-sort";
import type { SortState } from "@/features/identity-hub/lib/sort";
import { SortHeader } from "@/features/identity-hub/components/dashboard/shared/table/SortHeader";
import { RowActionsTrigger } from "@/features/identity-hub/components/dashboard/shared/RowActionsTrigger";
import {
  useIsSuperTenant,
  usePermissions,
} from "@/features/auth/hooks/usePermissions";
import {
  adminVendorsQueryOptions,
  useEnablePartnerLogin,
  useToggleAdminVendorActive,
  useUnlinkPartnerLogin,
} from "@/lib/queries";
import type { AdminVendorRow } from "@/features/admin/marketplace/types/admin-marketplace.types";
import { ImagePreviewTrigger } from "@/features/identity-hub/components/dashboard/ImagePreviewTrigger";
import { getVendorInitials } from "@/features/marketplace/lib/vendorInitials";
import {
  applyVendorListFilters,
  getVendorDisplayContactEmail,
  isVendorLinked,
  isVendorListFiltered,
  vendorSortValue,
  type VendorLinkedFilter,
  type VendorListFilters,
  type VendorSortKey,
  type VendorStatusFilter,
} from "../lib/admin-vendors-list.utils";

const SKELETON_ROWS = 5;
const DEFAULT_SORT: SortState<VendorSortKey> = { key: "name", dir: "asc" };
const TEXT_SORT_KEYS = new Set<VendorSortKey>(["name", "contactEmail"]);

const EMPTY_FILTERS: VendorListFilters = {
  search: "",
  linked: "all",
  status: "all",
};

function formatCreatedDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

interface AdminVendorsListProps {
  canList: boolean;
  canEdit: boolean;
  onEdit: (vendor: AdminVendorRow) => void;
}

export function AdminVendorsList({ canList, canEdit, onEdit }: AdminVendorsListProps) {
  const { t } = useTranslation();
  const perms = usePermissions();
  const isSuperTenant = useIsSuperTenant();
  const { data: vendors = [], isLoading, isError } = useQuery({
    ...adminVendorsQueryOptions(),
    enabled: canList,
  });
  const enablePartnerLogin = useEnablePartnerLogin();
  const unlinkPartnerLogin = useUnlinkPartnerLogin();
  const toggleActive = useToggleAdminVendorActive();

  const [filters, setFilters] = useState<VendorListFilters>(EMPTY_FILTERS);
  const [sort, setSort] = useState<SortState<VendorSortKey>>(DEFAULT_SORT);

  const canLink = isSuperTenant;
  const canUnlink = isSuperTenant;
  const canActivate = perms.MARKETPLACE_ADMIN_ACTIVATE_VENDOR.isOn;
  const canDeactivate = perms.MARKETPLACE_ADMIN_DEACTIVATE_VENDOR.isOn;

  const filteredVendors = useMemo(
    () => applyVendorListFilters(vendors, filters),
    [vendors, filters],
  );

  const sortedVendors = useMemo(
    () => sortTableRows(filteredVendors, sort, vendorSortValue),
    [filteredVendors, sort],
  );

  const isFiltered = isVendorListFiltered(filters);
  const rowActionPending =
    enablePartnerLogin.isPending ||
    unlinkPartnerLogin.isPending ||
    toggleActive.isPending;

  function handleSort(key: VendorSortKey) {
    setSort((prev) => toggleUsageSort(prev, key, TEXT_SORT_KEYS));
  }

  function handleUnlink(vendor: AdminVendorRow) {
    if (
      !window.confirm(
        t(
          "marketplace.admin.partner.unlinkConfirm",
          "Unlink this Partner org? The organization and people with Access stay; they just will not be tied to this partner.",
        ),
      )
    ) {
      return;
    }
    unlinkPartnerLogin.mutate(vendor.id);
  }

  if (!canList) {
    return (
      <p className="py-12 text-center text-sm text-muted-foreground">
        {t("common.noPermission", "You don't have permission to view this content.")}
      </p>
    );
  }

  if (isLoading) {
    return <AdminVendorsListSkeleton />;
  }

  if (isError) {
    return (
      <div className="py-12 text-center">
        <p className="font-medium">{t("marketplace.admin.errorTitle")}</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("errors.tryAgain", "Please try again.")}
        </p>
      </div>
    );
  }

  if (vendors.length === 0) {
    return (
      <EmptyState
        title={t("marketplace.admin.empty")}
        description={t(
          "marketplace.admin.emptyDescription",
          "Add your first partner to make it available in the catalog.",
        )}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <AdminFilterBar
        search={
          <AdminSearchInput
            value={filters.search}
            onChange={(search) => setFilters((prev) => ({ ...prev, search }))}
            placeholder={t(
              "marketplace.admin.searchPlaceholder",
              "Search name, slug, or email",
            )}
            label={t("marketplace.admin.searchLabel", "Search partners")}
            className="w-full sm:w-64"
          />
        }
        scopes={
          <>
            <Select
              value={filters.linked}
              onValueChange={(value) =>
                setFilters((prev) => ({
                  ...prev,
                  linked: value as VendorLinkedFilter,
                }))
              }
            >
              <SelectTrigger
                className="h-8 w-[160px] text-xs"
                aria-label={t(
                  "marketplace.admin.filters.linked",
                  "Partner org link",
                )}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("marketplace.admin.filters.linkedAll", "All link states")}
                </SelectItem>
                <SelectItem value="linked">
                  {t("marketplace.admin.filters.linkedYes", "Linked")}
                </SelectItem>
                <SelectItem value="unlinked">
                  {t("marketplace.admin.filters.linkedNo", "Not linked")}
                </SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.status}
              onValueChange={(value) =>
                setFilters((prev) => ({
                  ...prev,
                  status: value as VendorStatusFilter,
                }))
              }
            >
              <SelectTrigger
                className="h-8 w-[140px] text-xs"
                aria-label={t("marketplace.admin.filters.status", "Status")}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("marketplace.admin.filters.statusAll", "All statuses")}
                </SelectItem>
                <SelectItem value="active">
                  {t("marketplace.admin.status.active")}
                </SelectItem>
                <SelectItem value="inactive">
                  {t("marketplace.admin.status.inactive")}
                </SelectItem>
              </SelectContent>
            </Select>
          </>
        }
        isFiltered={isFiltered}
        onClear={() => setFilters(EMPTY_FILTERS)}
        resultCount={t("marketplace.admin.vendorCount", {
          count: sortedVendors.length,
          defaultValue_one: "{{count}} partner",
          defaultValue_other: "{{count}} partners",
        })}
      />

      {sortedVendors.length === 0 ? (
        <EmptyState
          title={t("marketplace.admin.noMatch", "No partners match")}
          description={t(
            "marketplace.admin.noMatchDescription",
            "Try a different search or clear the filters.",
          )}
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="group border-b border-border/50">
                <SortHeader
                  id="name"
                  label={t("marketplace.admin.table.name")}
                  sort={sort}
                  onSort={handleSort}
                  className="px-5 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70"
                />
                <SortHeader
                  id="contactEmail"
                  label={t("marketplace.admin.table.contactEmail")}
                  sort={sort}
                  onSort={handleSort}
                  className="hidden px-5 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70 md:table-cell"
                />
                <SortHeader
                  id="catalog"
                  label={t("marketplace.admin.table.catalog", {
                    defaultValue: "Catalog",
                  })}
                  sort={sort}
                  onSort={handleSort}
                  className="hidden px-5 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70 sm:table-cell"
                />
                <SortHeader
                  id="created"
                  label={t("marketplace.admin.table.created", {
                    defaultValue: "Created",
                  })}
                  sort={sort}
                  onSort={handleSort}
                  className="hidden px-5 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70 xl:table-cell"
                />
                <SortHeader
                  id="updated"
                  label={t("marketplace.admin.table.updated", {
                    defaultValue: "Updated",
                  })}
                  sort={sort}
                  onSort={handleSort}
                  className="hidden px-5 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70 lg:table-cell"
                />
                <SortHeader
                  id="status"
                  label={t("marketplace.admin.table.status")}
                  sort={sort}
                  onSort={handleSort}
                  className="px-5 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70"
                />
                <SortHeader
                  id="linked"
                  label={t("marketplace.admin.table.linked", {
                    defaultValue: "Linked",
                  })}
                  sort={sort}
                  onSort={handleSort}
                  className="px-5 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70"
                />
                <SortHeader
                  id="connected"
                  label={t("marketplace.admin.table.connected", {
                    defaultValue: "Connected",
                  })}
                  sort={sort}
                  onSort={handleSort}
                  className="hidden px-5 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70 md:table-cell"
                />
                <th className="px-5 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {sortedVendors.map((vendor) => (
                <VendorRow
                  key={vendor.id}
                  vendor={vendor}
                  canEdit={canEdit}
                  canLink={canLink}
                  canUnlink={canUnlink}
                  canActivate={canActivate}
                  canDeactivate={canDeactivate}
                  onEdit={() => onEdit(vendor)}
                  onLink={() => enablePartnerLogin.mutate(vendor.id)}
                  onUnlink={() => handleUnlink(vendor)}
                  onToggleActive={() =>
                    toggleActive.mutate({
                      vendorId: vendor.id,
                      activate: !vendor.isActive,
                    })
                  }
                  isToggling={
                    toggleActive.isPending &&
                    toggleActive.variables?.vendorId === vendor.id
                  }
                  isLinking={
                    enablePartnerLogin.isPending &&
                    enablePartnerLogin.variables === vendor.id
                  }
                  isUnlinking={
                    unlinkPartnerLogin.isPending &&
                    unlinkPartnerLogin.variables === vendor.id
                  }
                  anyRowActionPending={rowActionPending}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AdminVendorsListSkeleton() {
  return (
    <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
      <table className="w-full text-sm">
        <tbody className="divide-y divide-border/50">
          {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
            <tr key={i}>
              <td className="px-5 py-3.5">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-lg" />
                  <div className="flex flex-col gap-1.5">
                    <Skeleton className="h-3.5 w-32" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
              </td>
              <td className="hidden px-5 py-3.5 md:table-cell">
                <Skeleton className="h-3 w-40" />
              </td>
              <td className="hidden px-5 py-3.5 sm:table-cell">
                <Skeleton className="h-3 w-24" />
              </td>
              <td className="hidden px-5 py-3.5 xl:table-cell">
                <Skeleton className="h-3 w-20" />
              </td>
              <td className="hidden px-5 py-3.5 lg:table-cell">
                <Skeleton className="h-8 w-36" />
              </td>
              <td className="px-5 py-3.5">
                <Skeleton className="h-3 w-16" />
              </td>
              <td className="px-5 py-3.5">
                <Skeleton className="h-3 w-16" />
              </td>
              <td className="hidden px-5 py-3.5 md:table-cell">
                <Skeleton className="h-3 w-12" />
              </td>
              <td className="px-5 py-3.5">
                <Skeleton className="ml-auto h-7 w-7 rounded-md" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface VendorRowProps {
  vendor: AdminVendorRow;
  canEdit: boolean;
  canLink: boolean;
  canUnlink: boolean;
  canActivate: boolean;
  canDeactivate: boolean;
  onEdit: () => void;
  onLink: () => void;
  onUnlink: () => void;
  onToggleActive: () => void;
  isToggling: boolean;
  isLinking: boolean;
  isUnlinking: boolean;
  anyRowActionPending: boolean;
}

function VendorRow({
  vendor,
  canEdit,
  canLink,
  canUnlink,
  canActivate,
  canDeactivate,
  onEdit,
  onLink,
  onUnlink,
  onToggleActive,
  isToggling,
  isLinking,
  isUnlinking,
  anyRowActionPending,
}: VendorRowProps) {
  const { t } = useTranslation();
  const linked = isVendorLinked(vendor);
  const partnerName = vendor.partnerTenant?.name;
  const displayContactEmail = getVendorDisplayContactEmail(vendor);

  return (
    <tr
      className={cn(
        "group transition-colors duration-150 ease-out hover:bg-muted/40",
        canEdit && "cursor-pointer",
      )}
      onClick={canEdit ? onEdit : undefined}
    >
      <td className="px-5 py-3.5">
        <VendorIdentity vendor={vendor} canEdit={canEdit} onEdit={onEdit} />
      </td>
      <td className="hidden px-5 py-3.5 text-xs text-muted-foreground md:table-cell">
        {displayContactEmail ? (
          <div className="flex flex-col gap-0.5">
            <span className="text-foreground/80">{displayContactEmail}</span>
            {vendor.pendingContactEmail && (
              <span className="text-status-warning">
                {t("marketplace.admin.pendingEmail", {
                  email: vendor.pendingContactEmail,
                })}
              </span>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground/50">—</span>
        )}
      </td>
      <td className="hidden px-5 py-3.5 text-xs text-muted-foreground sm:table-cell">
        <span className="tabular-nums">
          {t("marketplace.admin.table.packagesCount", {
            count: vendor.packages.length,
            defaultValue: "{{count}} pkg",
            defaultValue_other: "{{count}} pkgs",
          })}
          <span className="px-1.5 text-muted-foreground/40">·</span>
          {t("marketplace.admin.table.questionsCount", {
            count: vendor.questions.length,
            defaultValue: "{{count}} question",
            defaultValue_other: "{{count}} questions",
          })}
        </span>
      </td>
      <td className="hidden px-5 py-3.5 text-xs text-muted-foreground xl:table-cell">
        <time dateTime={vendor.createdAt} className="tabular-nums whitespace-nowrap">
          {formatCreatedDate(vendor.createdAt)}
        </time>
      </td>
      <td className="hidden px-5 py-3.5 lg:table-cell">
        <VendorUpdatedCell vendor={vendor} />
      </td>
      <td className="px-5 py-3.5">
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium",
            vendor.isActive
              ? "bg-status-success-subtle text-status-success"
              : "bg-muted text-muted-foreground",
          )}
        >
          <span
            aria-hidden
            className={cn(
              "h-1.5 w-1.5 rounded-full",
              vendor.isActive ? "bg-status-success" : "bg-muted-foreground/40",
            )}
          />
          {vendor.isActive
            ? t("marketplace.admin.status.active")
            : t("marketplace.admin.status.inactive")}
        </span>
      </td>
      <td className="px-5 py-3.5">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span
            className={cn(
              "inline-flex w-fit items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium",
              linked
                ? "bg-status-success-subtle text-status-success"
                : "bg-muted text-muted-foreground",
            )}
          >
            {linked
              ? t("marketplace.admin.linked.yes", "Linked")
              : t("marketplace.admin.linked.no", "Not linked")}
          </span>
          {linked && partnerName ? (
            <span className="truncate text-xs text-muted-foreground">{partnerName}</span>
          ) : null}
        </div>
      </td>
      <td className="hidden px-5 py-3.5 md:table-cell">
        <span
          className={cn(
            "text-xs tabular-nums",
            (vendor.connectedCount ?? 0) === 0
              ? "text-muted-foreground/50"
              : "text-foreground/80",
          )}
        >
          {t("marketplace.admin.table.connectedCount", {
            count: vendor.connectedCount ?? 0,
            defaultValue: "{{count}} client",
            defaultValue_other: "{{count}} clients",
          })}
        </span>
      </td>
      <td
        className="w-12 px-5 py-3.5"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-end">
          <VendorRowMenu
            vendor={vendor}
            linked={linked}
            canEdit={canEdit}
            canLink={canLink}
            canUnlink={canUnlink}
            canActivate={canActivate}
            canDeactivate={canDeactivate}
            onEdit={onEdit}
            onLink={onLink}
            onUnlink={onUnlink}
            onToggleActive={onToggleActive}
            isToggling={isToggling}
            isLinking={isLinking}
            isUnlinking={isUnlinking}
            anyRowActionPending={anyRowActionPending}
          />
        </div>
      </td>
    </tr>
  );
}

interface VendorRowMenuProps {
  vendor: AdminVendorRow;
  linked: boolean;
  canEdit: boolean;
  canLink: boolean;
  canUnlink: boolean;
  canActivate: boolean;
  canDeactivate: boolean;
  onEdit: () => void;
  onLink: () => void;
  onUnlink: () => void;
  onToggleActive: () => void;
  isToggling: boolean;
  isLinking: boolean;
  isUnlinking: boolean;
  anyRowActionPending: boolean;
}

function VendorRowMenu({
  vendor,
  linked,
  canEdit,
  canLink,
  canUnlink,
  canActivate,
  canDeactivate,
  onEdit,
  onLink,
  onUnlink,
  onToggleActive,
  isToggling,
  isLinking,
  isUnlinking,
  anyRowActionPending,
}: VendorRowMenuProps) {
  const { t } = useTranslation();
  const canToggleActive = vendor.isActive ? canDeactivate : canActivate;
  const canPartnerAction = linked ? canUnlink : canLink;

  if (!canEdit && !canToggleActive && !canPartnerAction) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <RowActionsTrigger
          variant="visible"
          className="data-[state=open]:bg-muted data-[state=open]:text-foreground"
          aria-label={t("marketplace.admin.rowActions.menuAria", {
            name: vendor.name,
            defaultValue: "Actions for {{name}}",
          })}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {canEdit ? (
          <DropdownMenuItem icon="pencil" onSelect={onEdit}>
            {t("marketplace.admin.editVendor")}
          </DropdownMenuItem>
        ) : null}
        {canToggleActive ? (
          <DropdownMenuItem disabled={anyRowActionPending} onSelect={onToggleActive}>
            {vendor.isActive ? (
              <PowerOff className="size-3.5" aria-hidden />
            ) : (
              <Power className="size-3.5" aria-hidden />
            )}
            {isToggling
              ? vendor.isActive
                ? t("marketplace.admin.deactivating")
                : t("marketplace.admin.activating")
              : vendor.isActive
                ? t("marketplace.admin.deactivate")
                : t("marketplace.admin.activate")}
          </DropdownMenuItem>
        ) : null}
        {canPartnerAction ? (
          <>
            {canEdit || canToggleActive ? <DropdownMenuSeparator /> : null}
            {linked ? (
              <DropdownMenuItem disabled={anyRowActionPending} onSelect={onUnlink}>
                <Unlink className="size-3.5" aria-hidden />
                {isUnlinking
                  ? t("marketplace.admin.partner.unlinking", "Unlinking…")
                  : t("marketplace.admin.partner.unlink", "Unlink")}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                icon="link"
                disabled={anyRowActionPending}
                onSelect={onLink}
              >
                {isLinking
                  ? t("marketplace.admin.partner.linking", "Linking…")
                  : t("marketplace.admin.partner.link", "Link")}
              </DropdownMenuItem>
            )}
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function VendorUpdatedCell({ vendor }: { vendor: AdminVendorRow }) {
  const { t } = useTranslation();
  const actor = vendor.lastUpdatedBy;
  const dateLabel = formatCreatedDate(vendor.updatedAt);
  const date = (
    <time
      dateTime={vendor.updatedAt}
      className="tabular-nums whitespace-nowrap text-xs text-muted-foreground"
    >
      {dateLabel}
    </time>
  );

  if (!actor) {
    return date;
  }

  return (
    <span
      className="flex min-w-0 items-center gap-2"
      aria-label={t("marketplace.admin.table.updatedBy", {
        name: actor.name,
        date: dateLabel,
        defaultValue: "Updated {{date}} by {{name}}",
      })}
    >
      <UserAvatar
        name={actor.name}
        src={actor.photoUrl}
        size="sm"
        className="shrink-0"
      />
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-xs text-foreground/80">{actor.name}</span>
        {date}
      </span>
    </span>
  );
}

function VendorIdentity({
  vendor,
  canEdit,
  onEdit,
}: {
  vendor: AdminVendorRow;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const { t } = useTranslation();
  const logoClassName =
    "h-10 w-10 shrink-0 rounded-lg border border-border/60 bg-background object-contain";

  const logo = vendor.logoUrl ? (
    <ImagePreviewTrigger src={vendor.logoUrl} alt={vendor.name}>
      <img src={vendor.logoUrl} alt="" className={logoClassName} aria-hidden />
    </ImagePreviewTrigger>
  ) : (
    <div
      aria-hidden
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-xs font-semibold text-primary"
    >
      {getVendorInitials(vendor.name)}
    </div>
  );

  const nameBlock = (
    <div className="flex min-w-0 flex-col">
      <span className="truncate font-medium tracking-tight text-foreground">
        {vendor.name}
      </span>
      <span className="truncate font-mono text-xs text-muted-foreground">
        {vendor.slug}
      </span>
    </div>
  );

  return (
    <div className="flex items-center gap-3">
      {logo}
      {canEdit ? (
        <button
          type="button"
          className="min-w-0 flex-1 text-left"
          onClick={(event) => {
            event.stopPropagation();
            onEdit();
          }}
          aria-label={t("marketplace.admin.editVendor")}
        >
          {nameBlock}
        </button>
      ) : (
        nameBlock
      )}
    </div>
  );
}
